import { ref } from 'vue'
import axios from '@/plugins/axios'
import Swal from 'sweetalert2'

const API = '/api'

// ── Singleton ──
const descargas = ref([])
let contador = 0

export function useDescargas() {

  const quitarDescarga = (id) => {
    descargas.value = descargas.value.filter(x => x.id !== id)
  }

  // ═══════════════════════════════════════════════════
  // GENERAR AUTOEJECUTABLE
  // ═══════════════════════════════════════════════════
  const procesarAutoejecutable = async ({ tocId, nombre, convertirAPdf = false }) => {

    const id = ++contador
    descargas.value.push({ id, nombre, tipo: 'autoejecutable',
      estado: 'generando', progreso: 0, error: null })

    const buscar = () => descargas.value.find(x => x.id === id)
    const idUsuarioSesion = localStorage.getItem('idUsuario')

    try {
      // 1. Iniciar
      const resIni = await axios.post(
        `${API}/Doc/generar-autoejecutable-iniciar?idUsuarioSesion=${idUsuarioSesion}`,
        { tocId, rutaTemporal: 'C:\\Temp', rutaBDAccess: 'C:\\Temp\\BD', convertirImagenesAPdf: convertirAPdf }
      )
      const jobId = resIni.data.jobId || resIni.data.JobId
      if (!jobId) throw new Error('El servidor no devolvió un jobId')

      // 2. Polling progreso
      await new Promise((resolve, reject) => {
        const intervalo = setInterval(async () => {
          try {
            const resP = await axios.get(`${API}/Doc/generar-autoejecutable-progreso/${jobId}`)
            const data = resP.data
            const d = buscar()
            if (!d) { clearInterval(intervalo); return resolve() }
            d.progreso = data.porcentaje ?? data.Porcentaje ?? d.progreso
            const estado = (data.estado || data.Estado || '').toLowerCase()
            if (estado === 'completado') { clearInterval(intervalo); resolve() }
            else if (estado === 'error') { clearInterval(intervalo); reject(new Error(data.mensaje || data.Mensaje || 'Error')) }
          } catch (e) { clearInterval(intervalo); reject(e) }
        }, 1500)
      })

      // 3. Descargar
      const d = buscar(); if (d) d.estado = 'descargando'
      const resZip = await axios.get(
        `${API}/Doc/generar-autoejecutable-descargar/${jobId}`,
        { responseType: 'blob', timeout: 0 }
      )
      let nombreArchivo = `autoejecutable_${nombre}_${new Date().toISOString().slice(0, 10)}.zip`
      const cd = resZip.headers['content-disposition']
      if (cd) { const m = cd.match(/filename[^;=\n]*=["']?([^"';\n]+)["']?/i); if (m?.[1]) nombreArchivo = m[1].trim() }
      const url = window.URL.createObjectURL(new Blob([resZip.data]))
      const link = document.createElement('a'); link.href = url; link.setAttribute('download', nombreArchivo)
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url)
      const d2 = buscar(); if (d2) { d2.estado = 'completado'; d2.progreso = 100 }
      setTimeout(() => quitarDescarga(id), 8000)

    } catch (error) {
      const d = buscar()
      if (d) { d.estado = 'error'; d.error = error.response?.data?.mensaje || error.message || 'No se pudo generar' }
    }
  }

  // ═══════════════════════════════════════════════════
  // EXPORTAR CARPETA
  // ═══════════════════════════════════════════════════
  const procesarExportar = async ({ tocId, nombre, plantillaId, idUsuarioSesion }) => {
    const id = ++contador
    descargas.value.push({ id, nombre: `Exportar: ${nombre}`, tipo: 'exportar',
      estado: 'generando', progreso: 0, error: null })

    const buscar = () => descargas.value.find(x => x.id === id)

    try {
      // 1. Iniciar — el backend SIEMPRE responde JSON con jobId
      const resIni = await axios.post(
        `${API}/Toc/exportar?idUsuarioSesion=${idUsuarioSesion}`,
        { tocId, plantillaId: parseInt(plantillaId), conCertificado: false, clave: '' }
      )

      if (resIni.data.Success === false || resIni.data.success === false) {
        throw new Error(resIni.data.Mensaje || resIni.data.mensaje || 'No se pudo iniciar la exportación')
      }

      const jobId = resIni.data.jobId
      if (!jobId) throw new Error('El servidor no devolvió un jobId')

      // 2. Polling progreso cada 2s
      await new Promise((resolve, reject) => {
        const intervalo = setInterval(async () => {
          try {
            const resP = await axios.get(`${API}/Toc/exportar/estado/${jobId}`)
            const { status, progress, error } = resP.data
            const d = buscar()
            if (!d) { clearInterval(intervalo); return resolve() }
            d.progreso = progress ?? d.progreso
            if (status === 'completed') { clearInterval(intervalo); resolve() }
            else if (status === 'failed') { clearInterval(intervalo); reject(new Error(error || 'Error al exportar')) }
          } catch (e) { clearInterval(intervalo); reject(e) }
        }, 2000)
      })

      // 3. Descargar
      const d = buscar(); if (d) d.estado = 'descargando'
      const resZip = await axios.get(
        `${API}/Toc/exportar/descargar/${jobId}`,
        { responseType: 'blob', timeout: 0 }
      )
      let nombreArchivo = `${nombre}.zip`
      const cd = resZip.headers['content-disposition']
      if (cd) {
        const m = cd.match(/filename[^;=\n]*=["']?([^"';\n]+)["']?/i)
        if (m?.[1]) nombreArchivo = decodeURIComponent(m[1].trim())
      }
      const url = window.URL.createObjectURL(new Blob([resZip.data]))
      const link = document.createElement('a'); link.href = url; link.setAttribute('download', nombreArchivo)
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url)

      const d2 = buscar(); if (d2) { d2.estado = 'completado'; d2.progreso = 100 }

      // 🔔 Notificación — el usuario debe enterarse aunque el chip esté cerrado
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        iconColor: '#2e7d32',
        title: 'Exportación completada',
        text: `${nombre} se descargó correctamente`,
        timer: 3500,
        showConfirmButton: false,
        customClass: { container: 'swal-sobre-modal' }
      })

      setTimeout(() => quitarDescarga(id), 8000)

    } catch (error) {
      const d = buscar()
      if (d) {
        d.estado = 'error'
        d.error = error.response?.data?.Mensaje || error.response?.data?.mensaje || error.message || 'No se pudo exportar'
      }
    }
  }

  // ═══════════════════════════════════════════════════
  // Helpers de bajo nivel — para descargas que reportan
  // progreso real vía onDownloadProgress (el archivo YA existe,
  // no hay fase de "generación" como en autoejecutable/exportar)
  // ═══════════════════════════════════════════════════
  const registrarDescarga = (nombre, tipo = 'archivo') => {
    const id = ++contador
    descargas.value.push({ id, nombre, tipo, estado: 'descargando', progreso: 0, error: null })
    return id
  }

  const actualizarProgresoDescarga = (id, porcentaje) => {
    const d = descargas.value.find(x => x.id === id)
    if (d) d.progreso = porcentaje
  }

  const completarDescarga = (id, nombreFinal) => {
    const d = descargas.value.find(x => x.id === id)
    if (d) { d.estado = 'completado'; d.progreso = 100 }
    Swal.fire({
      toast: true, position: 'top-end', icon: 'success', iconColor: '#2e7d32',
      title: 'Descarga completada',
      text: nombreFinal ? `${nombreFinal} se descargó correctamente` : 'Se descargó correctamente',
      timer: 3500, showConfirmButton: false,
      customClass: { container: 'swal-sobre-modal' }
    })
    setTimeout(() => quitarDescarga(id), 8000)
  }

  const errorDescarga = (id, mensaje) => {
    const d = descargas.value.find(x => x.id === id)
    if (d) { d.estado = 'error'; d.error = mensaje || 'No se pudo descargar' }
  }

  // ═══════════════════════════════════════════════════
  // DESCARGAR ARCHIVO INDIVIDUAL (ya existe en disco, sin generación)
  // Sirve tanto para GET simple (video/audio original) como para
  // POST con body (ej: /api/Doc/descargar con certificado)
  // ═══════════════════════════════════════════════════
  const procesarDescargaArchivo = async ({ url, nombre, metodo = 'get', body = null }) => {
    const id = registrarDescarga(`Descargar: ${nombre}`, 'archivo')

    try {
      const config = {
        responseType: 'blob',
        timeout: 0,
        onDownloadProgress: (e) => {
          const total = e.total || Number(e.event?.target?.getResponseHeader?.('Content-Length')) || 0
          if (total > 0) actualizarProgresoDescarga(id, Math.round((e.loaded / total) * 100))
        }
      }

      const response = metodo === 'post'
        ? await axios.post(url, body, config)
        : await axios.get(url, config)

      // Si el backend devolvió un error en JSON dentro del blob
      const contentType = response.headers['content-type']
      if (contentType && contentType.includes('application/json')) {
        const text = await response.data.text()
        const errorData = JSON.parse(text)
        throw new Error(errorData.Mensaje || errorData.mensaje || 'Error al descargar el archivo')
      }

      // Nombre real desde Content-Disposition (ASP.NET File() lo setea solo)
      // Priorizar el nombre visible del documento y conservar
      // la extensión real del archivo devuelto por el backend.
      let nombreBackend = ''
      const cd = response.headers['content-disposition']

      if (cd) {
        const m = cd.match(/filename[^;=\n]*=["']?([^"';\n]+)["']?/i)

        if (m?.[1]) {
          nombreBackend = decodeURIComponent(m[1].trim())
        }
      }

      const nombreToc = typeof nombre === 'string' ? nombre.trim() : ''

      const extensionBackend = nombreBackend.match(/\.[^./\\]+$/)?.[0] || ''

      const nombreBase = nombreToc.replace(/\.[^./\\]+$/, '')

      const nombreArchivo = nombreToc
        ? (extensionBackend ? `${nombreBase}${extensionBackend}` : nombreToc)
        : (nombreBackend || 'archivo')

      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: contentType }))
      const link = document.createElement('a')
      link.href = blobUrl
      link.setAttribute('download', nombreArchivo)
      document.body.appendChild(link); link.click(); link.remove()
      window.URL.revokeObjectURL(blobUrl)

      completarDescarga(id, nombreArchivo)

    } catch (error) {
      errorDescarga(id, error.response?.data?.mensaje || error.response?.data?.Mensaje || error.message)
    }
  }

  return {
    descargas, procesarAutoejecutable, procesarExportar, procesarDescargaArchivo,
    registrarDescarga, actualizarProgresoDescarga, completarDescarga, errorDescarga,
    quitarDescarga
  }
}